<template>
  <div>
    <v-card-subtitle
      class="text-sm pa-0 pt-3 mb-2"
    >
      顧客
    </v-card-subtitle>
    <custom-combobox
      v-model="selectedCustomers"
      :items="displayItems"
      :filter="filtering"
      :search-input="searchQuery"
      multiple
      chips
      solo
      hide-details
      hide-selected
      dense
      return-object
      :menu-props="{ auto: true, offsetY: true }"
      :prepend-inner-icon="icons.mdiAccountMultiple"
      :loading="isLoading"
      :no-data-text="noDataText"
      :item-value="getCustomerId"
      :item-text="getCustomerName"
      :placeholder="placeholderText"
      :disabled="isTableCustomerMode && customers.length === 0"
      class="customer-select-combobox"
      @change="onSelectionChange"
      @update:search-input="onSearchInputUpdate"
    >
      <template #selection="{ attrs, item }">
        <v-chip
          v-bind="attrs"
          small
          close
          @click:close="removeCustomer(item)"
        >
          {{ item.attributes ? item.attributes.name : item.name }}<span v-if="item.attributes && item.attributes.memberNumber">({{ item.attributes.memberNumber }})</span>
        </v-chip>
      </template>

      <template #item="{ item }">
        <!-- 新規作成オプション -->
        <template v-if="item.isCreateNew">
          <v-list-item-action>
            <v-icon color="primary">
              {{ icons.mdiAccountPlus }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title class="primary--text font-weight-medium">
              「{{ searchQuery }}」で新規顧客を作成
            </v-list-item-title>
          </v-list-item-content>
        </template>
        <!-- 通常の顧客項目 -->
        <template v-else>
          <v-list-item-action>
            <v-checkbox
              hide-details
              disabled
              :ripple="false"
              :input-value="isSelected(item)"
            />
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title>
              {{ item.attributes ? item.attributes.name : item.name }}<span
                v-if="item.attributes && item.attributes.memberNumber"
                class="text--secondary"
              >(ID:{{ item.attributes.memberNumber }})</span>
            </v-list-item-title>
            <v-list-item-subtitle v-if="item.attributes && item.attributes.memo">
              {{ item.attributes.memo }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </template>
    </custom-combobox>
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance, inject,
} from '@vue/composition-api'
import { mdiAccountMultiple, mdiAccountPlus } from '@mdi/js'
import {
  filter,
  findIndex,
  includes,
  map,
  debounce,
  isEqual,
} from 'lodash'
import CustomerApi from '@/api/v2/admin/customer-management/Customer'
import CustomCombobox from '@/views/components/custom_vuetify/CustomCombobox'

export default {
  name: 'CustomerSelect',
  components: {
    CustomCombobox,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    initialCustomerIds: {
      type: Array,
      default: () => [],
    },
    tableCustomerIds: {
      type: Array,
      default: null,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    // 親から提供される顧客検索関数
    const searchCustomersFromParent = inject('searchCustomers', null)

    const isLoading = ref(false)
    const isCreating = ref(false)
    const customers = ref([])
    const selectedCustomers = ref([])
    const searchQuery = ref('')
    const isInitialized = ref(false)
    // 内部からの変更を追跡するフラグ
    const isInternalChange = ref(false)

    // テーブル顧客モード: tableCustomerIdsが明示的に渡された場合（空配列でも）
    const isTableCustomerMode = computed(() => {
      return props.tableCustomerIds !== null
    })

    const noDataText = computed(() => {
      if (isTableCustomerMode.value && customers.value.length === 0) {
        return 'テーブルに顧客が紐付いていません'
      }

      return '顧客が見つかりません'
    })

    const placeholderText = computed(() => {
      if (isTableCustomerMode.value) {
        return '顧客を選択...'
      }

      return '顧客を検索...'
    })

    // 表示用アイテム（検索結果 + 新規作成オプション）
    const displayItems = computed(() => {
      // テーブル顧客モードでは新規作成オプションを表示しない
      if (isTableCustomerMode.value) {
        return customers.value
      }

      // 検索クエリがある場合
      if (searchQuery.value && searchQuery.value.trim().length > 0) {
        const query = searchQuery.value.trim().toLowerCase()
        // 完全一致する顧客が存在するか確認
        const exactMatch = customers.value.some(c => {
          const name = c.attributes?.name?.toLowerCase() || ''

          return name === query
        })

        // 完全一致がない場合、新規作成オプションを追加
        if (!exactMatch && !isCreating.value) {
          return [
            ...customers.value,
            {
              id: '__create_new__',
              isCreateNew: true,
              name: searchQuery.value.trim(),
            },
          ]
        }
      }

      return customers.value
    })

    const selectedCustomerIds = computed(() => {
      return map(selectedCustomers.value, customer => {
        return Number(customer.id)
      })
    })

    const getCustomerId = item => {
      return Number(item.id)
    }

    const getCustomerName = item => {
      return item?.attributes?.name || item?.name || ''
    }

    const isSelected = item => {
      return includes(selectedCustomerIds.value, Number(item.id))
    }

    const fetchCustomers = async (query = '') => {
      // テーブルに顧客が紐付いている場合はAPIから取得しない
      if (isTableCustomerMode.value) return

      // 検索クエリがない場合は何もしない（初期ロードで全件取得しない）
      if (!query || query.trim().length === 0) {
        return
      }

      isLoading.value = true
      try {
        // 親から提供された検索関数があれば使う
        if (searchCustomersFromParent) {
          customers.value = await searchCustomersFromParent(query)
        } else {
          // フォールバック: 直接APIを呼ぶ
          const res = await CustomerApi.getCustomers({ query })
          if (res?.data?.data) {
            customers.value = res.data.data
          }
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error)
      } finally {
        isLoading.value = false
      }
    }

    const debouncedFetchCustomers = debounce(query => {
      fetchCustomers(query)
    }, 300)

    const loadTableCustomers = async customerIds => {
      if (!customerIds || customerIds.length === 0) return

      isLoading.value = true
      try {
        const customerPromises = customerIds.map(id => CustomerApi.getCustomer(id))
        const responses = await Promise.all(customerPromises)
        const loadedCustomers = responses
          .filter(res => res?.data?.data)
          .map(res => res.data.data)

        customers.value = loadedCustomers

        // 顧客が1人かつ初期選択がない場合は自動選択
        const hasNoInitialSelection = !props.initialCustomerIds || props.initialCustomerIds.length === 0
        if (loadedCustomers.length === 1 && hasNoInitialSelection && selectedCustomers.value.length === 0) {
          selectedCustomers.value = [loadedCustomers[0]]
          isInternalChange.value = true
          emit('input', selectedCustomerIds.value)
          emit('change', selectedCustomerIds.value)
        }
      } catch (error) {
        console.error('Failed to load table customers:', error)
      } finally {
        isLoading.value = false
      }
    }

    const loadInitialCustomers = async customerIds => {
      if (!customerIds || customerIds.length === 0) return

      isLoading.value = true
      try {
        // 個別のエラーを無視して成功したものだけを取得
        const customerPromises = customerIds.map(id => CustomerApi.getCustomer(id).catch(err => {
          console.warn(`Failed to load customer ${id}:`, err)

          return null
        }))
        const responses = await Promise.all(customerPromises)
        const loadedCustomers = responses
          .filter(res => res?.data?.data)
          .map(res => res.data.data)

        selectedCustomers.value = loadedCustomers

        // Merge loaded customers into the customers list (only if not in table customer mode)
        if (!isTableCustomerMode.value) {
          loadedCustomers.forEach(customer => {
            if (!customers.value.find(c => Number(c.id) === Number(customer.id))) {
              customers.value.push(customer)
            }
          })
        }
      } catch (error) {
        console.error('Failed to load initial customers:', error)
      } finally {
        isLoading.value = false
      }
    }

    const filtering = (item, queryText) => {
      // 新規作成オプションは常に表示
      if (item?.isCreateNew) return true

      if (!queryText) return true

      const name = item?.attributes?.name?.toString().toLowerCase() || ''
      const memo = item?.attributes?.memo?.toString().toLowerCase() || ''
      const memberNumber = item?.attributes?.memberNumber?.toString() || ''
      const query = queryText.toString().toLowerCase()

      return name.includes(query) || memo.includes(query) || memberNumber.includes(query)
    }

    const createNewCustomer = async name => {
      if (!name || name.trim().length === 0) return null

      isCreating.value = true
      try {
        const res = await CustomerApi.createCustomer({ name: name.trim() })
        if (res?.data?.data) {
          const newCustomer = res.data.data
          // ローカルの顧客リストに追加
          customers.value.push(newCustomer)
          vm.$toast.success(`顧客「${name.trim()}」を作成しました`)

          return newCustomer
        }
      } catch (error) {
        console.error('Failed to create customer:', error)
        vm.$toast.error('顧客の作成に失敗しました')
      } finally {
        isCreating.value = false
      }

      return null
    }

    const onSelectionChange = async items => {
      // 新規作成オプションが選択されたか確認
      const createNewItem = items.find(item => item.isCreateNew)
      if (createNewItem) {
        // 新規作成オプションを除外
        const otherItems = filter(items, item => !item.isCreateNew)
        selectedCustomers.value = otherItems

        // 新規顧客を作成
        const newCustomer = await createNewCustomer(createNewItem.name)
        if (newCustomer) {
          // 作成した顧客を選択に追加
          selectedCustomers.value.push(newCustomer)
        }

        // 検索クエリをクリア
        searchQuery.value = ''

        isInternalChange.value = true
        emit('input', selectedCustomerIds.value)
        emit('change', selectedCustomerIds.value)

        return
      }

      const validItems = filter(items, item => item && item.id && item.id !== '__create_new__')
      selectedCustomers.value = validItems
      isInternalChange.value = true
      emit('input', selectedCustomerIds.value)
      emit('change', selectedCustomerIds.value)
    }

    const removeCustomer = item => {
      const idx = findIndex(
        selectedCustomers.value,
        customer => Number(customer.id) === Number(item.id),
      )
      if (idx !== -1) {
        selectedCustomers.value.splice(idx, 1)
        isInternalChange.value = true
        emit('input', selectedCustomerIds.value)
        emit('change', selectedCustomerIds.value)
      }
    }

    const onSearchInputUpdate = value => {
      searchQuery.value = value
    }

    const reset = () => {
      selectedCustomers.value = []
      searchQuery.value = ''
    }

    // Watch search query for API search (only when not in table customer mode)
    watch(
      () => searchQuery.value,
      newVal => {
        if (isTableCustomerMode.value) return
        if (newVal && newVal.length >= 1) {
          debouncedFetchCustomers(newVal)
        }
      },
    )

    // Watch table customer IDs and load them when changed
    watch(
      () => props.tableCustomerIds,
      newVal => {
        // nullの場合はテーブル顧客モードではない
        if (newVal === null) return

        // 空配列の場合は顧客リストをクリア
        if (newVal.length === 0) {
          customers.value = []

          return
        }

        loadTableCustomers(newVal)
      },
      { immediate: true },
    )

    // Initialize with initial customer IDs
    watch(
      () => props.initialCustomerIds,
      (newVal, oldVal) => {
        // 内部からの変更の場合はスキップ
        if (isInternalChange.value) {
          isInternalChange.value = false

          return
        }

        // 初期化済みで値が変わっていない場合はスキップ
        if (isInitialized.value && isEqual(newVal, oldVal)) {
          return
        }

        // 値が空になった場合は選択をクリア
        if (!newVal || newVal.length === 0) {
          selectedCustomers.value = []
          isInitialized.value = true

          return
        }

        // 初回または外部からの変更の場合のみロード
        loadInitialCustomers(newVal).then(() => {
          isInitialized.value = true
        })
      },
      { immediate: true },
    )

    // 初期ロードでは顧客を取得しない（検索時のみAPIを呼ぶ）

    return {
      // data
      isLoading,
      isCreating,
      customers,
      selectedCustomers,
      searchQuery,

      // computed
      selectedCustomerIds,
      isTableCustomerMode,
      noDataText,
      placeholderText,
      displayItems,

      // methods
      getCustomerId,
      getCustomerName,
      isSelected,
      filtering,
      onSelectionChange,
      onSearchInputUpdate,
      removeCustomer,
      reset,
      createNewCustomer,

      icons: {
        mdiAccountMultiple,
        mdiAccountPlus,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.customer-select-combobox {
  // セレクトボックスが縦に潰れる問題を修正
  ::v-deep .v-input__slot {
    min-height: 40px !important;
  }

  ::v-deep .v-select__selections {
    min-height: 32px;
    padding: 4px 0;
  }

  // プレースホルダーが表示されるように入力フィールドの幅と高さを確保
  // ローカル開発環境ではフォーカス時のみmin-widthが設定されるため、
  // フォーカス状態に関係なく常に幅と高さを確保する
  ::v-deep .v-select__selections input {
    min-width: 100px !important;
    max-height: 32px !important;
  }
}
</style>
